import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WaitlistModal from "../components/WaitlistModal";

const PrivacyPolicy: React.FC = () => {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  return (
    <div className="min-h-screen bg-[#000000] text-white">
      <Helmet>
        <title>Privacy Policy | AmmoPantryAI</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header onJoinClick={() => setShowWaitlistModal(true)} />
      <WaitlistModal
        open={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
      />
      <div className="max-w-3xl mx-auto px-4 py-16">
      <h1 className="text-2xl font-bold mt-8 mb-2">Privacy Policy</h1>
        <p className="mb-4">Last updated: October&nbsp;28,&nbsp;2025</p>

        <h2 className="text-2xl font-bold mt-8 mb-2">Introduction</h2>
        <p className="mb-4">
          This Privacy Policy describes Our policies and procedures on the collection,
          use, and disclosure of Your information when You use the Service and tells
          You about Your privacy rights and how the law protects You.
        </p>
        <p className="mb-4">
          We’re committed to keeping Your data private: all information You enter into
          the Service is stored locally on Your Device, and We don’t collect or store
          it on Our servers. The only data We might retain is Your email if You
          contact Us, used solely to respond to You. By using the Service, You agree
          to the collection and use of information in accordance with this Privacy
          Policy.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">
          Interpretation and Definitions
        </h2>

        <h3 className="text-xl font-semibold mt-6 mb-2">Interpretation</h3>
        <p className="mb-4">
          The words of which the initial letter is capitalized have meanings defined
          under the following conditions. The following definitions shall have the
          same meaning regardless of whether they appear in singular or in plural.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Definitions</h3>
        <p className="mb-4">For the purposes of this Privacy Policy:</p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>
            <b>Account</b> means a unique account created for You to access our
            Service or parts of our Service.
          </li>
          <li>
            <b>Affiliate</b> means an entity that controls, is controlled by, or is
            under common control with a party, where &quot;control&quot; means
            ownership of 50% or more of the shares, equity interest, or other
            securities entitled to vote for election of directors or other managing
            authority.
          </li>
          <li>
            <b>Application</b> refers to any application or software program provided
            by the Company, including but not limited to <i>Ammo&nbsp;Pantry</i> and
            any other applications or software programs provided by the Company.
          </li>
          <li>
            <b>Company</b> (referred to as either &quot;the Company,&quot; &quot;We,&quot;
            &quot;Us,&quot; or &quot;Our&quot; in this Agreement) refers to Edifi Ventures, LLC.
            AmmoPantryAI, LLC is a D.B.A. of the company.
          </li>
          <li>
            <b>Country</b> refers to: United States.
          </li>
          <li>
            <b>Device</b> means any device that can access the Service, such as a
            computer, cellphone, or digital tablet.
          </li>
          <li>
            <b>Personal&nbsp;Data</b> is any information that relates to an identified
            or identifiable individual.
          </li>
          <li>
            <b>Service</b> refers to the Application.
          </li>
          <li>
            <b>Service&nbsp;Provider</b> means any natural or legal person who
            processes data on behalf of the Company.
          </li>
          <li>
            <b>Usage&nbsp;Data</b> refers to data collected automatically, either
            generated by the use of the Service or from the Service infrastructure
            itself.
          </li>
          <li>
            <b>You</b> means the individual accessing or using the Service, or the
            company or other legal entity on behalf of which such individual is
            accessing or using the Service, as applicable.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-2">Information We Collect</h2>
        <p className="mb-4">We may collect the following types of information:</p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>
            <b>Personal&nbsp;Information</b>: We may collect minimal personal
            information (e.g., email) only if You contact Us directly. No account is
            required to use the Service, and We don’t collect identifying details
            unless You provide them voluntarily.
          </li>
          <li>
            <b>App&nbsp;Data</b>: Information You enter into the Service (e.g.,
            inventory or usage details) is stored locally on Your Device and not sent
            to Our servers.
          </li>
          <li>
            <b>Usage&nbsp;Data</b>: We may collect anonymized data (e.g., app
            performance metrics) to enhance the Service. This may include Device type,
            crash reports, or feature usage, but nothing tied to Your identity or app
            inputs.
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          How We Use Your Information
        </h3>
        <p className="mb-4">We use your information to:</p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>Provide and maintain the Service, ensuring it works on Your Device.</li>
          <li>Respond to Your inquiries if You contact Us.</li>
          <li>Improve the Service through anonymized Usage&nbsp;Data analysis.</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-2">Local Storage and Cloud Backup</h2>
        <p className="mb-4">
          Data You input into the Service is stored locally on Your Device. If You have
          Cloud Backup enabled on Your device, this data may be automatically backed up to
          Your personal Cloud account as part of Your device's backup process. This backup
          is controlled by Your device settings and Apple's Cloud service, not by Our app.
          We don't access, store, or control this backup data.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">
          Retention of Your Personal Data
        </h2>
        <p className="mb-4">
          We keep Personal&nbsp;Data (e.g., Your email from a support request) only as
          long as needed to assist You. Usage&nbsp;Data is retained briefly for app
          improvements and is anonymized. App data You enter remains on Your Device
          until You delete it—We don't retain copies.
        </p>
        <p className="mb-4">
          If You have Cloud Backup enabled, Your app data may be retained in Your Cloud
          account according to Apple's backup policies, which We don't control.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">
          To delete Your Personal Data You can:
        </h2>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>
            Delete app data anytime by removing it from the Service or uninstalling
            the app.
          </li>
          <li>
            Request deletion of any Personal&nbsp;Data You've shared by emailing
            <a href="mailto:ammopantry@gmail.com" className="underline">&nbsp;ammopantry@gmail.com</a>.
          </li>
          <li>Manage Cloud backups through Your device settings. Note that Cloud backups are controlled by Apple, not by Our app.</li>
        </ul>
        <p className="mb-4">
          We'll comply unless legally required to retain it (e.g., for legal obligations).
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">
          Data Sharing and Disclosure
        </h2>
        <p className="mb-4">
          We do not sell your personal information. We may share information with:
        </p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>
            <b>Business Transactions</b>: If the Company is sold or merged, We’ll
            notify You before transferring any Personal&nbsp;Data (app data stays
            local regardless).
          </li>
          <li>
            <b>Law Enforcement</b>: We may disclose data only if required by law
            (e.g., court order) and will aim to notify You unless prohibited.
          </li>
          <li>
            <b>Other Needs</b>: We may share data to protect our rights, prevent fraud, or ensure safety, but only if legally necessary.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-2">Data Security</h2>
        <p className="mb-4">
          We prioritize your data's security. App data stays on your Device, under your control.
          If you have Cloud Backup enabled, your app data is backed up according to Apple's
          security standards. For any Personal Data You send us, We use reasonable safeguards,
          though no method is 100% secure. We strive to protect what You share.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">Your Rights</h2>
        <p className="mb-4">Depending on your location, you may have rights to:</p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>Access your personal information</li>
          <li>Correct inaccurate data</li>
          <li>Delete your information</li>
          <li>Restrict or object to processing</li>
          <li>Data portability</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-2">Children&#39;s Policy</h2>
        <p className="mb-4">
          Our Service isn’t for anyone under&nbsp;13. We don’t knowingly collect data
          from children. If You’re a parent and believe Your child shared data with
          Us, contact&nbsp;
          <a href="mailto:ammopantry@gmail.com" className="underline">
            ammopantry@gmail.com
          </a>
          &nbsp;and We’ll remove it.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">
          Links to Other Websites
        </h2>
        <p className="mb-4">
          The Service may link to third-party sites. We’re not responsible for their
          privacy practices—review their policies if You visit.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">
          Changes to This Policy
        </h2>
        <p className="mb-4">
          We may update this policy as the Service evolves. Changes will be posted
          here, with notice via the app or email if significant. Review periodically.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-2">Contact Us</h2>
        <p className="mb-4">
          If you have any questions about this Privacy Policy, please contact us at&nbsp;
          <a href="mailto:ammopantry@gmail.com" className="underline">
            ammopantry@gmail.com
          </a>
          .
        </p>

        {/* <p className="mb-4">This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.</p>
        <p className="mb-4">We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy. This Privacy Policy has been created with the help of the Privacy Policy Generator.</p>
        <h2 className="text-2xl font-bold mt-8 mb-2">Interpretation and Definitions</h2>
        <h3 className="text-xl font-semibold mt-6 mb-2">Interpretation</h3>
        <p className="mb-4">The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.</p>
        <h3 className="text-xl font-semibold mt-6 mb-2">Definitions</h3>
        <p className="mb-4">For the purposes of this Privacy Policy:</p>
        <ul className="list-disc list-inside mb-4">
          <li><b>Account</b> means a unique account created for You to access our Service or parts of our Service.</li>
          <li><b>Affiliate</b> means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</li>
          <li><b>Company</b> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to AmmoPantryAI LLC, Utah United States.</li>
          <li><b>Cookies</b> are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</li>
          <li><b>Country</b> refers to: United States</li>
          <li><b>Device</b> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
          <li><b>Personal Data</b> is any information that relates to an identified or identifiable individual.</li>
          <li><b>Service</b> refers to the Website.</li>
          <li><b>Service Provider</b> means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.</li>
          <li><b>Usage Data</b> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).</li>
          <li><b>Website</b> refers to AmmoPantryAI, accessible from ammopantryai.app</li>
          <li><b>You</b> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
        </ul>
        <h2 className="text-2xl font-bold mt-12 mb-4">Collecting and Using Your Personal Data</h2>

        <h3 className="text-xl font-semibold mt-8 mb-2">Types of Data Collected</h3>

        <h4 className="text-lg font-bold mt-6 mb-2">Personal Data</h4>
        <p className="mb-4">
          While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
        </p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>Email address</li>
          <li>First name and last name</li>
          <li>Phone number</li>
          <li>Usage Data</li>
        </ul>

        <h4 className="text-lg font-bold mt-6 mb-2">Usage Data</h4>
        <p className="mb-4">Usage Data is collected automatically when using the Service.</p>
        <p className="mb-4">Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.</p>
        <p className="mb-4">When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.</p>
        <p className="mb-4">We may also collect information that Your browser sends whenever You visit our Service or when You access the Service by or through a mobile device.</p>

        <h3 className="text-xl font-semibold mt-8 mb-2">Tracking Technologies and Cookies</h3>
        <p className="mb-4">
          We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to improve and analyze Our Service. The technologies We use may include:
        </p>
        <p className="mb-2">
          <b>Cookies or Browser Cookies.</b> A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your browser setting so that it will refuse Cookies, our Service may use Cookies.
        </p>
        <p className="mb-2">
          <b>Web Beacons.</b> Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of a certain section and verifying system and server integrity).
        </p>
        <p className="mb-4">
          Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on Your personal computer or mobile device when You go offline, while Session Cookies are deleted as soon as You close Your web browser. You can learn more about cookies on <a href="https://www.termsfeed.com/blog/cookies/" className="underline text-blue-600" target="_blank" rel="noopener noreferrer">TermsFeed website</a> article.
        </p>
        <p className="mb-4">
          We use both Session and Persistent Cookies for the purposes set out below:
        </p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>
            <b>Necessary / Essential Cookies</b><br />
            Type: Session Cookies<br />
            Administered by: Us<br />
            Purpose: These Cookies are essential to provide You with services available through the Website and to enable You to use some of its features. They help to authenticate users and prevent fraudulent use of user accounts. Without these Cookies, the services that You have asked for cannot be provided, and We only use these Cookies to provide You with those services.
          </li>
          <li className="mt-4">
            <b>Cookies Policy / Notice Acceptance Cookies</b><br />
            Type: Persistent Cookies<br />
            Administered by: Us<br />
            Purpose: These Cookies identify if users have accepted the use of cookies on the Website.
          </li>
          <li className="mt-4">
            <b>Functionality Cookies</b><br />
            Type: Persistent Cookies<br />
            Administered by: Us<br />
            Purpose: These Cookies allow us to remember choices You make when You use the Website, such as remembering your login details or language preference. The purpose of these Cookies is to provide You with a more personal experience and to avoid You having to re-enter your preferences every time You use the Website.
          </li>
        </ul>
        <p className="mb-4">
          For more information about the cookies we use and your choices regarding cookies, please visit our Cookies Policy or the Cookies section of our Privacy Policy.
        </p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Use of Your Personal Data</h2>
        <p className="mb-4">The Company may use Personal Data for the following purposes:</p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>To provide and maintain our Service, including to monitor the usage of our Service.</li>
          <li>To manage Your Account: to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user.</li>
          <li>For the performance of a contract: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.</li>
          <li>To contact You: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application's push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation.</li>
          <li>To provide You with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless You have opted not to receive such information.</li>
          <li>To manage Your requests: To attend and manage Your requests to Us.</li>
          <li>For business transfers: We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred.</li>
          <li>For other purposes: We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience.</li>
        </ul>

        <p className="mb-4">We may share Your personal information in the following situations:</p>
        <ul className="list-disc list-inside mb-4 ml-8">
          <li>With Service Providers: We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You.</li>
          <li>For business transfers: We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.</li>
          <li>With Affiliates: We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us.</li>
          <li>With business partners: We may share Your information with Our business partners to offer You certain products, services or promotions.</li>
          <li>With other users: when You share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.</li>
          <li>With Your consent: We may disclose Your personal information for any other purpose with Your consent.</li>
        </ul>

        <h2 className="text-2xl font-bold mt-12 mb-4">Retention of Your Personal Data</h2>
        <p className="mb-4">The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.</p>    
        <p className="mb-4">The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of Our Service, or We are legally obligated to retain this data for longer time periods.</p>
        
        <h2 className="text-2xl font-bold mt-12 mb-4">Transfer of Your Personal Data</h2>
        <p className="mb-4">Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of Your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.</p>
        <p className="mb-4">Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer.</p>
        <p className="mb-4">The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy and no transfer of Your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of Your data and other personal information.</p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Delete Your Personal Data</h2>
        <p className="mb-4">You have the right to delete—or to ask us to help delete—the Personal Data we have collected about you.</p>
        <p className="mb-4">Our Service may let you remove certain information directly from within your account. You can update, amend, or delete your information at any time by:</p>
        <p className="mb-4">You may update, amend, or delete Your information at any time by signing in to Your Account, if you have one, and visiting the account settings section that allows you to manage Your personal information. You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us.</p>
        <p className="mb-4">Please note, however, that We may need to retain certain information when we have a legal obligation or lawful basis to do so.</p>

    
        <h2 className="text-2xl font-bold mt-12 mb-4">Disclosure of Your Personal Data</h2>
        <h3 className="text-xl font-bold mt-12 mb-4">Business Transactions</h3>
        <p className="mb-4">If the Company is involved in a merger, acquisition, or asset sale, your Personal Data may be transferred. We will notify you before the transfer and before your data becomes subject to a different Privacy Policy.</p>

        <h3 className="text-xl font-bold mt-12 mb-4">Law Enforcement</h3>
        <p className="mb-4">Under certain circumstances, we may be required to disclose your Personal Data—e.g., to comply with a court order or a government request.</p>

        <h3 className="text-xl font-bold mt-12 mb-4">Other Legal Requirements</h3>
        <p className="mb-4">We may disclose your Personal Data in the good-faith belief that such action is necessary to:</p>
        <ul>
        <li>Comply with a legal obligation</li>
        <li>Protect and defend the rights or property of the Company</li>
        <li>Prevent or investigate possible wrongdoing related to the Service</li>
        <li>Protect the personal safety of users or the public</li>
        <li>Protect against legal liability</li>
        </ul>
      

        <h2 className="text-2xl font-bold mt-12 mb-4">Security of Your Personal Data</h2>
        <p className="mb-4">We care about the security of your data, but remember that no method of transmission over the Internet or electronic storage is 100&nbsp;% secure. While we use commercially acceptable means to protect your Personal Data, we cannot guarantee absolute security.</p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Children’s Privacy</h2>
        <p className="mb-4">Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.</p>
        <p className="mb-4">If We need to rely on consent as a legal basis for processing Your information and Your country requires consent from a parent, We may require Your parent's consent before We collect and use that information.</p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Links to Other Websites</h2>
        <p className="mb-4">Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.</p>
        <p className="mb-4">We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.</p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Changes to This Privacy Policy</h2>
        <p className="mb-4">We may update this Privacy Policy from time to time. We will:</p>
        <ul>
        <li>Post any changes on this page</li>
        <li>Provide notice via email and/or a prominent notice within our Service before changes take effect</li>
        <li>Update the “Last updated” date at the top of the policy</li>
        </ul>
        <p className="mb-4">Please review this Privacy Policy periodically. Changes become effective once posted here.</p> */}
      </div>
      <Footer
        onSignUpClick={() => setShowWaitlistModal(true)}
      />
    </div>
  );
};

export default PrivacyPolicy; 